import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Checkbox, FormControlLabel, FormGroup,
  IconButton, Tooltip, Box, Typography, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Paper,
  Alert, Chip, CircularProgress, InputAdornment, Card, CardContent,
  Divider, MenuItem, Select, FormControl, InputLabel, Grid
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Undo as UndoIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { medicineAPI, salesAPI, billingAPI } from '../services/api';

const FREQUENCIES = ['OD', 'BD', 'TDS', 'QID'];

const emptyForm = {
  name: '',
  genericName: '',
  defaultDosage: '',
  commonFrequencies: []
};

// Helper to safely parse items JSON array
const parseItems = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) return p;
      if (typeof p === 'string') {
        const p2 = JSON.parse(p);
        if (Array.isArray(p2)) return p2;
      }
    } catch (e) {
      return [];
    }
  }
  return [];
};

function MedicineManagement() {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Delete confirm dialog
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ─── Refund / Return Medicine Policy State ─────────────────────────────────
  const [refundOpen, setRefundOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [searchingInvoices, setSearchingInvoices] = useState(false);
  const [foundInvoices, setFoundInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [refundItems, setRefundItems] = useState([]);
  const [refundReason, setRefundReason] = useState('Unopened / Customer Return');
  const [otherReason, setOtherReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountType, setDiscountType] = useState('percent'); // 'percent' or 'amount'
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [refundSuccess, setRefundSuccess] = useState(null);

  // ─── Past Refunds History State ───
  const [historyOpen, setHistoryOpen] = useState(false);
  const [refundHistory, setRefundHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historySearch, setHistorySearch] = useState('');

  useEffect(() => {
    fetchMedicines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchMedicines = async () => {
    try {
      setLoading(true);
      const response = await medicineAPI.getAll({ search: searchTerm, limit: 1000 });
      setMedicines(response.data.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch medicines');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await medicineAPI.create(formData);
      setSuccess('Medicine added successfully!');
      setFormData(emptyForm);
      setShowForm(false);
      fetchMedicines();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add medicine');
    } finally {
      setLoading(false);
    }
  };

  const handleFrequencyChange = (freq, data, setData) => {
    const frequencies = data.commonFrequencies || [];
    setData({
      ...data,
      commonFrequencies: frequencies.includes(freq)
        ? frequencies.filter(f => f !== freq)
        : [...frequencies, freq]
    });
  };

  const openEdit = (medicine) => {
    setEditId(medicine.id);
    setEditData({
      name: medicine.name || '',
      genericName: medicine.genericName || '',
      defaultDosage: medicine.defaultDosage || '',
      commonFrequencies: medicine.commonFrequencies || []
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editData.name.trim()) return;
    setEditLoading(true);
    try {
      await medicineAPI.update(editId, editData);
      setSuccess('Medicine updated successfully!');
      setEditOpen(false);
      fetchMedicines();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update medicine');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await medicineAPI.delete(deleteTarget.id);
      setSuccess('Medicine deleted successfully!');
      setDeleteTarget(null);
      fetchMedicines();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete medicine');
    }
  };

  // ─── Refund / Return Medicine Handlers ─────────────────────────────────────

  const handleOpenRefundModal = () => {
    setRefundOpen(true);
    setCustomerSearchTerm('');
    setFoundInvoices([]);
    setSelectedInvoice(null);
    setRefundItems([]);
    setRefundReason('Unopened / Customer Return');
    setOtherReason('');
    setReasonError('');
    setDiscountPercent(0);
    setDiscountAmount(0);
    setDiscountType('percent');
    setRefundError('');
    setRefundSuccess(null);
    // Auto-fetch recent sales to show immediately
    searchInvoices('');
  };

  const searchInvoices = async (query = customerSearchTerm) => {
    setSearchingInvoices(true);
    setRefundError('');
    try {
      // 1. Fetch sales matching query
      const salesPromise = salesAPI.getAll({ search: query.trim() || undefined }).catch(() => ({ data: [] }));
      // 2. Fetch invoices matching query
      const invPromise = billingAPI.getInvoices({ search: query.trim() || undefined, type: 'Sale' }).catch(() => ({ data: [] }));

      const [salesRes, invRes] = await Promise.all([salesPromise, invPromise]);

      const salesList = Array.isArray(salesRes.data) ? salesRes.data : [];
      const invoicesList = Array.isArray(invRes.data) ? invRes.data : [];

      // Combine and unify records
      const combined = [];
      const seenBillNumbers = new Set();

      salesList.forEach(sale => {
        const billNo = sale.billNumber;
        seenBillNumbers.add(billNo);
        combined.push({
          source: 'sale',
          id: sale.id,
          saleId: sale.id,
          billNumber: billNo,
          invoiceNumber: billNo,
          customerName: sale.customerName || (sale.patient ? `${sale.patient.firstName} ${sale.patient.lastName}` : 'Walk-in Customer'),
          customerPhone: sale.customerPhone || sale.patient?.phone || '',
          saleDate: sale.saleDate ? new Date(sale.saleDate).toLocaleDateString('en-IN') : '-',
          items: parseItems(sale.items),
          subtotal: parseFloat(sale.subtotal) || parseFloat(sale.totalAmount) || 0,
          discount: parseFloat(sale.discount) || 0,
          discountPercentage: parseFloat(sale.discountPercentage) || 0,
          totalAmount: parseFloat(sale.totalAmount) || 0,
          paidAmount: parseFloat(sale.paidAmount) || 0,
          paymentStatus: sale.paymentStatus || 'Paid',
          paymentMethod: sale.paymentMethod || 'Cash',
          soldBy: sale.soldBy || 'Staff'
        });
      });

      invoicesList.forEach(inv => {
        if (!seenBillNumbers.has(inv.invoiceNumber)) {
          seenBillNumbers.add(inv.invoiceNumber);
          combined.push({
            source: 'invoice',
            id: inv.id,
            invoiceId: inv.id,
            saleId: inv.saleId || null,
            billNumber: inv.invoiceNumber,
            invoiceNumber: inv.invoiceNumber,
            customerName: inv.patientName || (inv.patient ? `${inv.patient.firstName} ${inv.patient.lastName}` : 'Customer'),
            customerPhone: inv.patientPhone || inv.patient?.phone || '',
            saleDate: inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString('en-IN') : '-',
            items: parseItems(inv.items),
            subtotal: parseFloat(inv.subtotal) || parseFloat(inv.totalAmount) || 0,
            discount: parseFloat(inv.discount) || 0,
            discountPercentage: parseFloat(inv.discountPercentage) || 0,
            totalAmount: parseFloat(inv.totalAmount) || 0,
            paidAmount: parseFloat(inv.paidAmount) || 0,
            paymentStatus: inv.paymentStatus || 'Paid',
            paymentMethod: 'Cash',
            soldBy: inv.createdBy || 'Staff'
          });
        }
      });

      setFoundInvoices(combined);
    } catch (err) {
      console.error('Error searching invoices:', err);
      setRefundError('Failed to search invoices. Please try again.');
    } finally {
      setSearchingInvoices(false);
    }
  };

  // Click on an invoice -> Auto-fills all details and medicine prices automatically
  const handleSelectInvoice = (inv) => {
    setSelectedInvoice(inv);
    setRefundError('');
    setRefundSuccess(null);
    setOtherReason('');
    setReasonError('');

    // Auto-detect discount from invoice
    const invDiscPct = parseFloat(inv.discountPercentage) || 0;
    const invDiscAmt = parseFloat(inv.discount) || 0;
    const invSubtotal = parseFloat(inv.subtotal) || parseFloat(inv.totalAmount) || 0;

    if (invDiscPct > 0) {
      setDiscountPercent(invDiscPct);
      setDiscountType('percent');
      setDiscountAmount(parseFloat(((invSubtotal * invDiscPct) / 100).toFixed(2)));
    } else if (invDiscAmt > 0 && invSubtotal > 0) {
      const calcPct = (invDiscAmt / invSubtotal) * 100;
      setDiscountPercent(parseFloat(calcPct.toFixed(2)));
      setDiscountAmount(invDiscAmt);
      setDiscountType('percent');
    } else {
      setDiscountPercent(0);
      setDiscountAmount(0);
      setDiscountType('percent');
    }

    const items = inv.items || [];
    const normalized = items.map((it, idx) => {
      const medName = it.medicineName || it.name || it.description || `Medicine #${idx + 1}`;
      const unitPrice = parseFloat(it.unitPrice || it.price || (it.amount && it.quantity ? it.amount / it.quantity : 0)) || 0;
      const qty = parseFloat(it.quantity || 1);
      return {
        id: it.id || idx,
        medicineId: it.medicineId || it.id || null,
        medicineName: medName,
        purchasedQty: qty,
        unitPrice: unitPrice,
        isLoose: !!it.isLoose,
        unitsPerPackage: it.unitsPerPackage || 1,
        selected: true, // Selected for refund by default
        returnQty: qty, // Default to full quantity returned
        refundTotal: parseFloat((qty * unitPrice).toFixed(2))
      };
    });

    setRefundItems(normalized);
  };

  const handleToggleItemSelection = (index) => {
    const updated = [...refundItems];
    updated[index].selected = !updated[index].selected;
    setRefundItems(updated);
  };

  const handleReturnQtyChange = (index, value) => {
    const updated = [...refundItems];
    let qty = parseFloat(value);
    if (isNaN(qty) || qty < 0) qty = 0;
    if (qty > updated[index].purchasedQty) qty = updated[index].purchasedQty;

    updated[index].returnQty = qty;
    updated[index].refundTotal = parseFloat((qty * updated[index].unitPrice).toFixed(2));
    if (qty > 0) {
      updated[index].selected = true;
    }
    setRefundItems(updated);
  };

  const calculateRefundSubtotal = () => {
    return refundItems
      .filter(it => it.selected && it.returnQty > 0)
      .reduce((sum, it) => sum + (it.returnQty * it.unitPrice), 0);
  };

  const calculateDiscountValue = () => {
    const subtotal = calculateRefundSubtotal();
    if (discountType === 'percent') {
      return (subtotal * (parseFloat(discountPercent) || 0)) / 100;
    } else {
      return Math.min(subtotal, parseFloat(discountAmount) || 0);
    }
  };

  const calculateNetRefund = () => {
    const subtotal = calculateRefundSubtotal();
    const discountVal = calculateDiscountValue();
    return Math.max(0, subtotal - discountVal);
  };

  const handleSubmitRefund = async () => {
    const activeReturns = refundItems.filter(it => it.selected && it.returnQty > 0);
    if (activeReturns.length === 0) {
      setRefundError('Please select at least one medicine with return quantity > 0.');
      return;
    }

    // Reason validation (Compulsory!)
    if (!refundReason || !refundReason.trim()) {
      setReasonError('Reason for return is compulsory.');
      setRefundError('Reason for return is compulsory. Please select or write a reason.');
      return;
    }

    let finalReason = refundReason.trim();
    if (refundReason === 'Other') {
      if (!otherReason.trim()) {
        setReasonError('Please write the reason for return (compulsory).');
        setRefundError('Please write the reason for return when "Other" is selected.');
        return;
      }
      finalReason = `Other: ${otherReason.trim()}`;
    }

    const subtotal = calculateRefundSubtotal();
    const discountVal = calculateDiscountValue();
    const netRefund = calculateNetRefund();

    if (netRefund <= 0 && subtotal <= 0) {
      setRefundError('Total refund amount must be greater than zero.');
      return;
    }

    setRefundSubmitting(true);
    setRefundError('');
    setReasonError('');

    try {
      const payload = {
        saleId: selectedInvoice.saleId || (selectedInvoice.source === 'sale' ? selectedInvoice.id : null),
        invoiceId: selectedInvoice.source === 'invoice' ? selectedInvoice.id : (selectedInvoice.invoiceId || null),
        billNumber: selectedInvoice.billNumber || selectedInvoice.invoiceNumber,
        customerName: selectedInvoice.customerName,
        customerPhone: selectedInvoice.customerPhone,
        returnedItems: activeReturns.map(it => ({
          medicineId: it.medicineId,
          medicineName: it.medicineName,
          quantityReturned: it.returnQty,
          unitPrice: it.unitPrice,
          refundAmount: parseFloat((it.returnQty * it.unitPrice).toFixed(2)),
          isLoose: it.isLoose,
          unitsPerPackage: it.unitsPerPackage
        })),
        subtotal: parseFloat(subtotal.toFixed(2)),
        discount: parseFloat(discountVal.toFixed(2)),
        discountPercentage: discountType === 'percent' ? parseFloat(discountPercent) : parseFloat(((discountVal / (subtotal || 1)) * 100).toFixed(2)),
        totalRefundAmount: parseFloat(netRefund.toFixed(2)),
        refundMethod: 'Cash', // Strictly Cash refund as customer is paid in cash
        reason: finalReason,
        refundDate: new Date().toISOString().substring(0, 10)
      };

      const response = await salesAPI.refund(payload);

      setRefundSuccess({
        ...payload,
        ...response.data?.refundDetails,
        subtotal: parseFloat(subtotal.toFixed(2)),
        discount: parseFloat(discountVal.toFixed(2)),
        totalRefundAmount: parseFloat(netRefund.toFixed(2)),
        reason: finalReason,
        message: response.data?.message || `Refund of ₹${netRefund.toFixed(2)} processed successfully in Cash!`
      });

      // Refresh medicines catalog
      fetchMedicines();
    } catch (err) {
      console.error('Error submitting refund:', err);
      setRefundError(err.response?.data?.error || err.message || 'Failed to process refund. Please try again.');
    } finally {
      setRefundSubmitting(false);
    }
  };

  // Print official Cash Refund Voucher
  const handlePrintRefundReceipt = () => {
    if (!refundSuccess) return;
    const items = refundSuccess.returnedItems || [];
    const dateStr = refundSuccess.refundDate || new Date().toISOString().substring(0, 10);
    const voucherNo = refundSuccess.refundReference || `REFUND-${refundSuccess.billNumber}`;

    const itemsHTML = items.map(it => `
      <tr>
        <td style="padding: 6px; border-bottom: 1px solid #ddd;">${it.medicineName}</td>
        <td style="padding: 6px; text-align: center; border-bottom: 1px solid #ddd;">${it.quantityReturned}</td>
        <td style="padding: 6px; text-align: right; border-bottom: 1px solid #ddd;">₹${parseFloat(it.unitPrice || 0).toFixed(2)}</td>
        <td style="padding: 6px; text-align: right; border-bottom: 1px solid #ddd; font-weight: bold;">₹${parseFloat(it.refundAmount || 0).toFixed(2)}</td>
      </tr>
    `).join('');

    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Cash Refund Voucher - ${voucherNo}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: A5; margin: 8mm; }
            body { font-family: 'Arial', sans-serif; font-size: 10pt; color: #222; background: #fff; padding: 12px; }
            .bill { border: 1.5px solid #000; padding: 15px; border-radius: 4px; }
            .header { text-align: center; border-bottom: 2px dashed #444; padding-bottom: 10px; margin-bottom: 10px; }
            .header h1 { font-size: 14pt; font-weight: bold; margin-bottom: 3px; }
            .header h2 { font-size: 9pt; font-weight: normal; margin-bottom: 4px; }
            .badge-title { display: inline-block; background: #e65100; color: #fff; font-weight: bold; font-size: 9pt; padding: 3px 10px; border-radius: 4px; margin-top: 5px; }
            .meta { display: flex; justify-content: space-between; margin: 10px 0; font-size: 9pt; }
            .meta-box { flex: 1; line-height: 1.5; }
            table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 9pt; }
            th { background: #f0f0f0; border-bottom: 1.5px solid #000; padding: 6px; text-align: left; }
            .totals { border-top: 2px solid #000; margin-top: 10px; padding-top: 8px; }
            .row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 9pt; }
            .final { font-size: 12pt; font-weight: bold; color: #b71c1c; border-top: 1px dashed #666; padding-top: 6px; margin-top: 6px; }
            .footer { margin-top: 30px; display: flex; justify-content: space-between; font-size: 9pt; }
            .sig-line { width: 140px; border-top: 1px solid #000; text-align: center; padding-top: 4px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="bill">
            <div class="header">
              <h1>GLOW with SuperDoc</h1>
              <h2>MSG Hotels & Resorts, Sirsa, Haryana | Pin: 125055</h2>
              <div><span class="badge-title">MEDICINE RETURN & CASH REFUND VOUCHER</span></div>
            </div>

            <div class="meta">
              <div class="meta-box">
                <p><strong>Customer Name:</strong> ${refundSuccess.customerName || 'Customer'}</p>
                <p><strong>Phone:</strong> ${refundSuccess.customerPhone || '-'}</p>
                <p><strong>Original Bill/Invoice:</strong> ${refundSuccess.billNumber || '-'}</p>
              </div>
              <div class="meta-box" style="text-align: right;">
                <p><strong>Voucher No:</strong> ${voucherNo}</p>
                <p><strong>Refund Date:</strong> ${dateStr}</p>
                <p><strong>Payment Mode:</strong> <span style="font-weight:bold; color:#2e7d32;">CASH (Paid Out)</span></p>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Returned Medicine</th>
                  <th style="text-align: center;">Qty</th>
                  <th style="text-align: right;">Unit Price</th>
                  <th style="text-align: right;">Refund Amount</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHTML}
              </tbody>
            </table>

            <div class="totals">
              <div class="row">
                <span>Subtotal of Returned Items:</span>
                <span>₹${parseFloat(refundSuccess.subtotal || refundSuccess.totalRefundAmount || 0).toFixed(2)}</span>
              </div>
              ${parseFloat(refundSuccess.discount || 0) > 0 ? `
              <div class="row" style="color: #b71c1c;">
                <span>Discount (${refundSuccess.discountPercentage || 0}%):</span>
                <span>-₹${parseFloat(refundSuccess.discount || 0).toFixed(2)}</span>
              </div>` : ''}
              <div class="row">
                <span>Reason for Return:</span>
                <span>${refundSuccess.reason || 'Customer Return'}</span>
              </div>
              <div class="row">
                <span>Payment Mode:</span>
                <span><strong>Cash Reimbursement</strong></span>
              </div>
              <div class="row final">
                <span>TOTAL CASH REFUND PAID:</span>
                <span>₹${parseFloat(refundSuccess.totalRefundAmount || 0).toFixed(2)}</span>
              </div>
            </div>

            <p style="font-size: 8pt; color: #555; margin-top: 10px; font-style: italic;">
              * Note: Returned medicines have been restored to inventory stock. Cash amount ₹${parseFloat(refundSuccess.totalRefundAmount || 0).toFixed(2)} paid to customer in cash and deducted from daily cash register.
            </p>

            <div class="footer">
              <div class="sig-line">Customer Signature</div>
              <div class="sig-line">Pharmacist / Reception</div>
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); };
          </script>
        </body>
      </html>
    `;

    const printWin = window.open('', '_blank', 'width=650,height=700');
    if (printWin) {
      printWin.document.open();
      printWin.document.write(printHTML);
      printWin.document.close();
    }
  };

  // ─── Past Refunds History Handlers ───
  const handleOpenRefundHistory = () => {
    setHistoryOpen(true);
    setHistorySearch('');
    fetchRefundHistory('');
  };

  const fetchRefundHistory = async (searchQuery = '') => {
    setLoadingHistory(true);
    try {
      const res = await salesAPI.getRefunds({ search: searchQuery.trim() || undefined });
      setRefundHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching refund history:', err);
      try {
        const payRes = await billingAPI.getPayments();
        const payments = payRes.data?.payments || [];
        const filtered = payments.filter(p =>
          parseFloat(p.amount) < 0 || (p.reference && p.reference.includes('REFUND'))
        );
        const mapped = filtered.map(p => ({
          id: p.id,
          voucherNumber: p.reference,
          invoiceNumber: p.invoice?.invoiceNumber || '-',
          billNumber: p.reference?.replace('REFUND-', '') || p.invoice?.invoiceNumber,
          customerName: p.invoice?.patientName || (p.invoice?.patient ? `${p.invoice.patient.firstName} ${p.invoice.patient.lastName}` : 'Customer'),
          customerPhone: p.invoice?.patientPhone || p.invoice?.patient?.phone || '',
          refundAmount: Math.abs(parseFloat(p.amount) || 0),
          paymentMode: p.mode || 'Cash',
          paymentDate: p.paymentDate,
          processedBy: p.receivedBy || 'Staff',
          notes: p.notes,
          reason: p.notes ? (p.notes.match(/\[(.*?)\]/)?.[1] || p.notes) : 'Customer Return',
          items: p.invoice?.items || []
        }));
        setRefundHistory(mapped);
      } catch (fallbackErr) {
        console.error('Fallback payments query failed:', fallbackErr);
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  const printRefundVoucherDirect = (refund) => {
    const items = refund.items || [];
    const dateStr = refund.paymentDate || new Date().toISOString().substring(0, 10);
    const voucherNo = refund.voucherNumber || `REFUND-${refund.billNumber}`;

    const itemsHTML = items.length > 0 ? items.map(it => `
      <tr>
        <td style="padding: 6px; border-bottom: 1px solid #ddd;">${it.medicineName || it.name || it.description || 'Medicine'}</td>
        <td style="padding: 6px; text-align: center; border-bottom: 1px solid #ddd;">${it.quantity || it.quantityReturned || 1}</td>
        <td style="padding: 6px; text-align: right; border-bottom: 1px solid #ddd;">₹${parseFloat(it.unitPrice || it.price || 0).toFixed(2)}</td>
        <td style="padding: 6px; text-align: right; border-bottom: 1px solid #ddd; font-weight: bold;">₹${parseFloat(it.total || it.amount || it.refundAmount || 0).toFixed(2)}</td>
      </tr>
    `).join('') : `
      <tr>
        <td colspan="4" style="padding: 8px; text-align: center; font-style: italic; color: #777;">Details recorded under voucher ${voucherNo}</td>
      </tr>
    `;

    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Cash Refund Voucher - ${voucherNo}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: A5; margin: 8mm; }
            body { font-family: 'Arial', sans-serif; font-size: 10pt; color: #222; background: #fff; padding: 12px; }
            .bill { border: 1.5px solid #000; padding: 15px; border-radius: 4px; }
            .header { text-align: center; border-bottom: 2px dashed #444; padding-bottom: 10px; margin-bottom: 10px; }
            .header h1 { font-size: 14pt; font-weight: bold; margin-bottom: 3px; }
            .header h2 { font-size: 9pt; font-weight: normal; margin-bottom: 4px; }
            .badge-title { display: inline-block; background: #e65100; color: #fff; font-weight: bold; font-size: 9pt; padding: 3px 10px; border-radius: 4px; margin-top: 5px; }
            .meta { display: flex; justify-content: space-between; margin: 10px 0; font-size: 9pt; }
            .meta-box { flex: 1; line-height: 1.5; }
            table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 9pt; }
            th { background: #f0f0f0; border-bottom: 1.5px solid #000; padding: 6px; text-align: left; }
            .totals { border-top: 2px solid #000; margin-top: 10px; padding-top: 8px; }
            .row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 9pt; }
            .final { font-size: 12pt; font-weight: bold; color: #b71c1c; border-top: 1px dashed #666; padding-top: 6px; margin-top: 6px; }
            .footer { margin-top: 30px; display: flex; justify-content: space-between; font-size: 9pt; }
            .sig-line { width: 140px; border-top: 1px solid #000; text-align: center; padding-top: 4px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="bill">
            <div class="header">
              <h1>GLOW with SuperDoc</h1>
              <h2>MSG Hotels & Resorts, Sirsa, Haryana | Pin: 125055</h2>
              <div><span class="badge-title">MEDICINE RETURN & CASH REFUND VOUCHER</span></div>
            </div>

            <div class="meta">
              <div class="meta-box">
                <p><strong>Customer Name:</strong> ${refund.customerName || 'Customer'}</p>
                <p><strong>Phone:</strong> ${refund.customerPhone || '-'}</p>
                <p><strong>Original Bill/Invoice:</strong> ${refund.billNumber || '-'}</p>
              </div>
              <div class="meta-box" style="text-align: right;">
                <p><strong>Voucher No:</strong> ${voucherNo}</p>
                <p><strong>Refund Date:</strong> ${dateStr}</p>
                <p><strong>Payment Mode:</strong> <span style="font-weight:bold; color:#2e7d32;">CASH (Paid Out)</span></p>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Returned Medicine</th>
                  <th style="text-align: center;">Qty</th>
                  <th style="text-align: right;">Unit Price</th>
                  <th style="text-align: right;">Refund Amount</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHTML}
              </tbody>
            </table>

            <div class="totals">
              <div class="row">
                <span>Reason for Return:</span>
                <span>${refund.reason || 'Customer Return'}</span>
              </div>
              <div class="row">
                <span>Payment Mode:</span>
                <span><strong>Cash Reimbursement</strong></span>
              </div>
              <div class="row final">
                <span>TOTAL CASH REFUND PAID:</span>
                <span>₹${parseFloat(refund.refundAmount || 0).toFixed(2)}</span>
              </div>
            </div>

            <p style="font-size: 8pt; color: #555; margin-top: 10px; font-style: italic;">
              * Note: Returned medicines restored to inventory stock. Cash amount ₹${parseFloat(refund.refundAmount || 0).toFixed(2)} paid to customer in cash and deducted from daily cash register.
            </p>

            <div class="footer">
              <div class="sig-line">Customer Signature</div>
              <div class="sig-line">Pharmacist / Reception</div>
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); };
          </script>
        </body>
      </html>
    `;

    const printWin = window.open('', '_blank', 'width=650,height=700');
    if (printWin) {
      printWin.document.open();
      printWin.document.write(printHTML);
      printWin.document.close();
    }
  };

  const subtotalRefund = calculateRefundSubtotal();
  const discountVal = calculateDiscountValue();
  const netRefund = calculateNetRefund();

  return (
    <div className="card">
      <h2>💊 Medicine Management</h2>

      {error && <div className="error-message">❌ {error}</div>}
      {success && <div className="success-message">✅ {success}</div>}

      <div className="btn-group" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Hide Form' : '+ Add New Medicine'}
        </button>

        {/* ── Prominent Refund Policy Button ── */}
        <Button
          variant="contained"
          onClick={handleOpenRefundModal}
          startIcon={<UndoIcon />}
          sx={{
            backgroundColor: '#e65100',
            color: '#fff',
            fontWeight: 'bold',
            padding: '8px 18px',
            borderRadius: '6px',
            textTransform: 'none',
            fontSize: '0.95rem',
            boxShadow: '0 3px 6px rgba(230, 81, 0, 0.3)',
            '&:hover': {
              backgroundColor: '#bf360c'
            }
          }}
        >
          🔄Return Medicines
        </Button>

        {/* ── Small Button to View Past Refunded Index ── */}
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpenRefundHistory}
          startIcon={<HistoryIcon />}
          sx={{
            borderColor: '#0288d1',
            color: '#0288d1',
            fontWeight: '600',
            padding: '6px 14px',
            borderRadius: '6px',
            textTransform: 'none',
            fontSize: '0.875rem',
            '&:hover': {
              borderColor: '#01579b',
              backgroundColor: '#e1f5fe'
            }
          }}
        >
          📜 View Return History
        </Button>
      </div>

      {showForm && (
        <div className="card" style={{ marginTop: '2rem', background: '#f8f9fa' }}>
          <h3>Add New Medicine</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Medicine Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Generic Name</label>
                <input
                  type="text"
                  value={formData.genericName}
                  onChange={(e) => setFormData({ ...formData, genericName: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Default Dosage</label>
                <input
                  type="text"
                  value={formData.defaultDosage}
                  onChange={(e) => setFormData({ ...formData, defaultDosage: e.target.value })}
                  placeholder="e.g., 500mg, 10ml, etc."
                />
              </div>
            </div>
            <div className="form-group">
              <label>Common Frequencies</label>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                {FREQUENCIES.map(freq => (
                  <label key={freq} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={(formData.commonFrequencies || []).includes(freq)}
                      onChange={() => handleFrequencyChange(freq, formData, setFormData)}
                    />
                    <span>{freq}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="btn-group">
              <button type="submit" className="btn btn-success" disabled={loading}>
                {loading ? 'Adding...' : 'Add Medicine'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="search-box" style={{ marginTop: '2rem' }}>
        <input
          type="text"
          placeholder="Search medicines..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading && !showForm ? (
        <div className="loading">Loading medicines...</div>
      ) : medicines.length === 0 ? (
        <div>{searchTerm ? 'No medicines found matching your search.' : 'No medicines added yet.'}</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Generic Name</th>
                <th>Default Dosage</th>
                <th>Common Frequencies</th>
                <th>Usage Count</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {medicines.map(medicine => (
                <tr key={medicine.id}>
                  <td><strong>{medicine.name}</strong></td>
                  <td>{medicine.genericName || '-'}</td>
                  <td>{medicine.defaultDosage || '-'}</td>
                  <td>
                    {medicine.commonFrequencies?.length > 0
                      ? medicine.commonFrequencies.join(', ')
                      : '-'}
                  </td>
                  <td><span className="badge badge-info">{medicine.usageCount}</span></td>
                  <td>
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => openEdit(medicine)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(medicine)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Medicine</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth size="small" label="Medicine Name *" sx={{ mb: 2, mt: 1 }}
            value={editData.name}
            onChange={e => setEditData({ ...editData, name: e.target.value })}
          />
          <TextField
            fullWidth size="small" label="Generic Name" sx={{ mb: 2 }}
            value={editData.genericName}
            onChange={e => setEditData({ ...editData, genericName: e.target.value })}
          />
          <TextField
            fullWidth size="small" label="Default Dosage" sx={{ mb: 2 }}
            placeholder="e.g., 500mg, 10ml"
            value={editData.defaultDosage}
            onChange={e => setEditData({ ...editData, defaultDosage: e.target.value })}
          />
          <div style={{ marginTop: '0.5rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Common Frequencies</label>
            <FormGroup row sx={{ mt: 0.5 }}>
              {FREQUENCIES.map(freq => (
                <FormControlLabel
                  key={freq}
                  label={freq}
                  control={
                    <Checkbox
                      size="small"
                      checked={(editData.commonFrequencies || []).includes(freq)}
                      onChange={() => handleFrequencyChange(freq, editData, setEditData)}
                    />
                  }
                />
              ))}
            </FormGroup>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editLoading || !editData.name.trim()}>
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Medicine?</DialogTitle>
        <DialogContent>
          Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* ── REFUND MEDICINE POLICY DIALOG (Full Interactive Workflow) ────────── */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <Dialog
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px', minHeight: '600px' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UndoIcon color="warning" />
            <Typography variant="h6" fontWeight="bold">
              Medicine Return & Cash Refund Policy
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<HistoryIcon />}
              onClick={() => {
                setRefundOpen(false);
                handleOpenRefundHistory();
              }}
              sx={{
                borderColor: '#0288d1',
                color: '#0288d1',
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '0.8rem',
                borderRadius: '6px',
                '&:hover': {
                  borderColor: '#01579b',
                  backgroundColor: '#e1f5fe'
                }
              }}
            >
              📜 View Past Refunded Index
            </Button>
            <IconButton size="small" onClick={() => setRefundOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: '#fafafa' }}>
          {/* Policy Information Alert */}
          <Alert severity="info" sx={{ mb: 2.5, fontSize: '0.85rem' }}>
            <strong>Policy Details:</strong> When a customer returns medicine, search their name or bill number to find their purchase invoice. Clicking the invoice automatically populates all medicines and prices. Upon submission, <strong>stock is restored to inventory</strong> and <strong>money is refunded in Cash</strong> (deducted from the daily cash balance).
          </Alert>

          {refundError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRefundError('')}>
              {refundError}
            </Alert>
          )}

          {/* ── State 1: Refund Success Confirmation ── */}
          {refundSuccess ? (
            <Paper elevation={0} sx={{ p: 3, border: '1.5px solid #2e7d32', backgroundColor: '#e8f5e9', borderRadius: '8px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 36 }} />
                <div>
                  <Typography variant="h6" color="#2e7d32" fontWeight="bold">
                    Return & Cash Refund Completed Successfully!
                  </Typography>
                  <Typography variant="body2" color="#1b5e20">
                    {refundSuccess.message}
                  </Typography>
                </div>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2} sx={{ fontSize: '0.9rem' }}>
                <Grid item xs={12} sm={6}>
                  <p><strong>Customer Name:</strong> {refundSuccess.customerName || 'Customer'}</p>
                  <p><strong>Original Bill No:</strong> {refundSuccess.billNumber || '-'}</p>
                  <p><strong>Refund Voucher No:</strong> {refundSuccess.refundReference || '-'}</p>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <p><strong>Payment Mode:</strong> <Chip label="Cash (Paid to Customer)" size="small" color="success" /></p>
                  <p><strong>Refund Date:</strong> {refundSuccess.refundDate || new Date().toISOString().substring(0, 10)}</p>
                  <p><strong>Cash Paid Out:</strong> <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#b71c1c' }}>₹{parseFloat(refundSuccess.totalRefundAmount || 0).toFixed(2)}</span></p>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  Returned Items Restored to Inventory:
                </Typography>
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #c8e6c9' }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: '#c8e6c9' }}>
                      <TableRow>
                        <TableCell><strong>Medicine Name</strong></TableCell>
                        <TableCell align="center"><strong>Qty Returned</strong></TableCell>
                        <TableCell align="right"><strong>Unit Price</strong></TableCell>
                        <TableCell align="right"><strong>Total Refund</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(refundSuccess.returnedItems || []).map((it, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{it.medicineName}</TableCell>
                          <TableCell align="center">{it.quantityReturned}</TableCell>
                          <TableCell align="right">₹{parseFloat(it.unitPrice || 0).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{parseFloat(it.refundAmount || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintRefundReceipt}
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                  Print Cash Refund Voucher
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleOpenRefundModal}
                  sx={{ textTransform: 'none' }}
                >
                  Process Another Return
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<HistoryIcon />}
                  onClick={() => {
                    setRefundOpen(false);
                    handleOpenRefundHistory();
                  }}
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                  View Past Refunded Index
                </Button>
              </Box>
            </Paper>
          ) : !selectedInvoice ? (
            /* ── State 2: Search Customer & Select Invoice ── */
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Step 1: Search Customer by Name or Bill / Invoice Number
              </Typography>

              <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter Customer Name, Patient Name, or Bill Number (e.g., Shipra, Sukhchain, BILL-20251228-0001)..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') searchInvoices(customerSearchTerm); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => searchInvoices(customerSearchTerm)}
                  disabled={searchingInvoices}
                  sx={{ px: 3, textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  {searchingInvoices ? <CircularProgress size={20} color="inherit" /> : 'Search'}
                </Button>
              </Box>

              {searchingInvoices ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : foundInvoices.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', color: '#666', border: '1px dashed #ccc' }}>
                  <ReceiptIcon sx={{ fontSize: 48, color: '#aaa', mb: 1 }} />
                  <Typography variant="body1">
                    {customerSearchTerm
                      ? `No billing/invoice/sale found matching "${customerSearchTerm}".`
                      : 'Search by customer name above to load available invoices.'}
                  </Typography>
                </Paper>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Found {foundInvoices.length} matching billing/sales invoice(s). <strong>Click an invoice to auto-fill medicine prices and proceed:</strong>
                  </Typography>

                  <TableContainer component={Paper} elevation={1}>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: '#eceff1' }}>
                        <TableRow>
                          <TableCell><strong>Bill / Invoice #</strong></TableCell>
                          <TableCell><strong>Customer / Patient</strong></TableCell>
                          <TableCell><strong>Phone</strong></TableCell>
                          <TableCell><strong>Sale Date</strong></TableCell>
                          <TableCell align="right"><strong>Total Amount</strong></TableCell>
                          <TableCell align="center"><strong>Items</strong></TableCell>
                          <TableCell align="center"><strong>Action</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {foundInvoices.map((inv) => (
                          <TableRow
                            key={inv.id + (inv.billNumber || '')}
                            hover
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: '#fff3e0' }
                            }}
                            onClick={() => handleSelectInvoice(inv)}
                          >
                            <TableCell>
                              <Chip
                                label={inv.billNumber || inv.invoiceNumber}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </TableCell>
                            <TableCell><strong>{inv.customerName}</strong></TableCell>
                            <TableCell>{inv.customerPhone || '-'}</TableCell>
                            <TableCell>{inv.saleDate}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              ₹{inv.totalAmount.toFixed(2)}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={(inv.items || []).map(i => `${i.medicineName || i.description || i.name} x ${i.quantity}`).join(', ')}>
                                <Chip label={`${(inv.items || []).length} item(s)`} size="small" />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                sx={{ textTransform: 'none', py: 0.2 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectInvoice(inv);
                                }}
                              >
                                Select & Auto-Fill
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : (
            /* ── State 3: Selected Invoice & Auto-Filled Return Form ── */
            <Box>
              {/* Selected Invoice Banner */}
              <Card sx={{ mb: 2.5, backgroundColor: '#fff8e1', border: '1px solid #ffe082' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                    <div>
                      <Typography variant="subtitle1" fontWeight="bold" color="#b78103">
                        ✅ Invoice Selected: {selectedInvoice.billNumber || selectedInvoice.invoiceNumber}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Customer Name:</strong> {selectedInvoice.customerName} &nbsp;|&nbsp;
                        <strong>Phone:</strong> {selectedInvoice.customerPhone || '-'} &nbsp;|&nbsp;
                        <strong>Sale Date:</strong> {selectedInvoice.saleDate} &nbsp;|&nbsp;
                        <strong>Total Bill:</strong> ₹{selectedInvoice.totalAmount.toFixed(2)}
                      </Typography>
                    </div>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => { setSelectedInvoice(null); setRefundItems([]); }}
                      sx={{ textTransform: 'none' }}
                    >
                      ← Change Invoice
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
                Step 2: Medicines Auto-Filled. Select Medicines & Quantities to Return:
              </Typography>

              {/* Medicines Return Table */}
              <TableContainer component={Paper} elevation={1} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <span style={{ fontWeight: 'bold' }}>Return?</span>
                      </TableCell>
                      <TableCell><strong>Medicine Name</strong></TableCell>
                      <TableCell align="center"><strong>Purchased Qty</strong></TableCell>
                      <TableCell align="right"><strong>Unit Price (₹)</strong></TableCell>
                      <TableCell align="center"><strong>Return Qty</strong></TableCell>
                      <TableCell align="right"><strong>Refund Amount (₹)</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {refundItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: '#777' }}>
                          No medicine items found in this invoice.
                        </TableCell>
                      </TableRow>
                    ) : (
                      refundItems.map((item, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor: item.selected ? '#f1f8e9' : 'transparent',
                            '&:hover': { backgroundColor: item.selected ? '#e8f5e9' : '#fafafa' }
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={item.selected}
                              onChange={() => handleToggleItemSelection(index)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <strong>{item.medicineName}</strong>
                            {item.isLoose && <Chip label="Loose" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={item.purchasedQty} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            ₹{item.unitPrice.toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={item.returnQty}
                              onChange={(e) => handleReturnQtyChange(index, e.target.value)}
                              disabled={!item.selected}
                              inputProps={{
                                min: 0,
                                max: item.purchasedQty,
                                step: item.isLoose ? 1 : 1,
                                style: { textAlign: 'center', width: '60px', padding: '4px' }
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: item.selected ? '#2e7d32' : '#999' }}>
                            ₹{item.refundTotal.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Refund Method, Discount & Cash Reduction Summary */}
              <Paper elevation={0} sx={{ p: 2.5, backgroundColor: '#fff3e0', border: '1.5px solid #ffb74d', borderRadius: '8px' }}>
                <Grid container spacing={2.5}>
                  {/* Left Column: Reason for Return & Payment Mode */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" required error={Boolean(reasonError && !refundReason)} sx={{ mb: 1.5 }}>
                      <InputLabel>Reason for Return * (Compulsory)</InputLabel>
                      <Select
                        value={refundReason}
                        label="Reason for Return * (Compulsory)"
                        onChange={(e) => {
                          setRefundReason(e.target.value);
                          if (e.target.value !== 'Other') {
                            setReasonError('');
                          } else if (!otherReason.trim()) {
                            setReasonError('Please write the reason for return below.');
                          }
                        }}
                      >
                        <MenuItem value="Unopened / Customer Return">Unopened / Customer Return</MenuItem>
                        <MenuItem value="Patient Allergy / Adverse Reaction">Patient Allergy / Adverse Reaction</MenuItem>
                        <MenuItem value="Doctor Changed Prescription">Doctor Changed Prescription</MenuItem>
                        <MenuItem value="Excess Medicine Purchased">Excess Medicine Purchased</MenuItem>
                        <MenuItem value="Incorrect Medicine Dispensed">Incorrect Medicine Dispensed</MenuItem>
                        <MenuItem value="Other">Other (Specify below)</MenuItem>
                      </Select>
                      {reasonError && !refundReason && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {reasonError}
                        </Typography>
                      )}
                    </FormControl>

                    {/* Compulsory Other Reason Text Input - Opens when 'Other' is selected */}
                    {refundReason === 'Other' && (
                      <TextField
                        fullWidth
                        size="small"
                        required
                        multiline
                        rows={2}
                        label="Write Reason for Return * (Compulsory)"
                        placeholder="Please write the compulsory reason why medicine is being returned..."
                        value={otherReason}
                        onChange={(e) => {
                          setOtherReason(e.target.value);
                          if (e.target.value.trim()) {
                            setReasonError('');
                          } else {
                            setReasonError('Please write the reason for return.');
                          }
                        }}
                        error={Boolean(reasonError && refundReason === 'Other')}
                        helperText={
                          (reasonError && refundReason === 'Other')
                            ? reasonError
                            : "Compulsory: Enter reason when 'Other' is selected"
                        }
                        sx={{ mb: 1.5, backgroundColor: '#fff' }}
                      />
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Payment Mode:
                      </Typography>
                      <Chip
                        label="💵 CASH REFUND"
                        color="success"
                        sx={{ fontWeight: 'bold', letterSpacing: '0.5px' }}
                      />
                    </Box>
                    <Typography variant="caption" color="#d32f2f" sx={{ display: 'block', mt: 0.5, fontWeight: 'bold' }}>
                      * Paid to customer in Cash. Deducted directly from daily cash balance.
                    </Typography>
                  </Grid>

                  {/* Right Column: Discount & Refund Breakdown */}
                  <Grid item xs={12} sm={6}>
                    {/* Discount Control Box */}
                    <Box sx={{ p: 1.5, mb: 1.5, backgroundColor: '#ffffff', borderRadius: '6px', border: '1px solid #ffe0b2' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" color="#e65100">
                          🏷️ Return Discount:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button
                            size="small"
                            variant={discountType === 'percent' ? 'contained' : 'outlined'}
                            onClick={() => {
                              setDiscountType('percent');
                              setDiscountAmount(parseFloat(calculateDiscountValue().toFixed(2)));
                            }}
                            sx={{ minWidth: '45px', py: 0.2, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
                          >
                            %
                          </Button>
                          <Button
                            size="small"
                            variant={discountType === 'amount' ? 'contained' : 'outlined'}
                            onClick={() => {
                              setDiscountType('amount');
                              setDiscountAmount(parseFloat(calculateDiscountValue().toFixed(2)));
                            }}
                            sx={{ minWidth: '45px', py: 0.2, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
                          >
                            ₹
                          </Button>
                        </Box>
                      </Box>

                      <Grid container spacing={1} alignItems="center">
                        {discountType === 'percent' ? (
                          <Grid item xs={7}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Discount %"
                              value={discountPercent}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setDiscountPercent(isNaN(val) ? 0 : Math.max(0, Math.min(100, val)));
                              }}
                              inputProps={{ min: 0, max: 100, step: 1 }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                              }}
                            />
                          </Grid>
                        ) : (
                          <Grid item xs={7}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Discount ₹"
                              value={discountAmount}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setDiscountAmount(isNaN(val) ? 0 : Math.max(0, val));
                              }}
                              inputProps={{ min: 0, step: 1 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>
                              }}
                            />
                          </Grid>
                        )}
                        <Grid item xs={5}>
                          <Typography variant="body2" color="error" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                            -₹{discountVal.toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>

                      {selectedInvoice?.discountPercentage > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                          (Original bill discount: {selectedInvoice.discountPercentage}%)
                        </Typography>
                      )}
                    </Box>

                    {/* Net Total Box */}
                    <Box sx={{ textAlign: 'right', p: 1.5, backgroundColor: '#ffffff', borderRadius: '6px', border: '1px solid #ffe0b2' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#555', mb: 0.5 }}>
                        <span>Subtotal of medicines:</span>
                        <span>₹{subtotalRefund.toFixed(2)}</span>
                      </Box>
                      {discountVal > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#b71c1c', mb: 0.5 }}>
                          <span>Less Discount ({discountType === 'percent' ? `${discountPercent}%` : `₹${discountVal.toFixed(2)}`}):</span>
                          <span>-₹{discountVal.toFixed(2)}</span>
                        </Box>
                      )}
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="body2" color="text.secondary" fontWeight="bold">
                        TOTAL CASH REFUND TO PAY:
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="#e65100">
                        ₹{netRefund.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {refundItems.filter(i => i.selected && i.returnQty > 0).length} medicine(s) selected
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, backgroundColor: '#fff' }}>
          <Button onClick={() => setRefundOpen(false)} disabled={refundSubmitting}>
            {refundSuccess ? 'Close' : 'Cancel'}
          </Button>

          {!refundSuccess && selectedInvoice && (
            <Button
              variant="contained"
              onClick={handleSubmitRefund}
              disabled={
                refundSubmitting ||
                netRefund <= 0 ||
                !refundReason ||
                (refundReason === 'Other' && !otherReason.trim())
              }
              sx={{
                backgroundColor: '#e65100',
                color: '#fff',
                fontWeight: 'bold',
                px: 3,
                textTransform: 'none',
                '&:hover': { backgroundColor: '#bf360c' }
              }}
            >
              {refundSubmitting ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Processing Cash Refund...
                </>
              ) : (
                `Submit Return & Refund ₹${netRefund.toFixed(2)} (Cash)`
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* ── PAST REFUNDS HISTORY DIALOG (Index of all past returns) ─────────── */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px', minHeight: '550px' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Past Medicine Refunds & Returns History
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setHistoryOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: '#fafafa' }}>
          {/* Summary Metric Chips & Search */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip
                label={`Total Past Refunds: ${refundHistory.length}`}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 'bold' }}
              />
              <Chip
                label={`Total Cash Refunded: ₹${refundHistory.reduce((sum, r) => sum + (parseFloat(r.refundAmount) || 0), 0).toFixed(2)}`}
                sx={{ backgroundColor: '#ffebee', color: '#b71c1c', fontWeight: 'bold' }}
              />
            </Box>

            <Box sx={{ minWidth: '300px' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search past refunds by customer, bill, or reason..."
                value={historySearch}
                onChange={(e) => {
                  setHistorySearch(e.target.value);
                  fetchRefundHistory(e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          </Box>

          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : refundHistory.length === 0 ? (
            <Paper sx={{ p: 5, textAlign: 'center', color: '#777', border: '1px dashed #ccc' }}>
              <ReceiptIcon sx={{ fontSize: 48, color: '#aaa', mb: 1 }} />
              <Typography variant="body1">
                {historySearch ? `No past refunds found matching "${historySearch}".` : 'No past refunds recorded yet.'}
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={1}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Voucher #</strong></TableCell>
                    <TableCell><strong>Original Bill #</strong></TableCell>
                    <TableCell><strong>Customer / Patient</strong></TableCell>
                    <TableCell><strong>Phone</strong></TableCell>
                    <TableCell><strong>Compulsory Reason</strong></TableCell>
                    <TableCell align="center"><strong>Mode</strong></TableCell>
                    <TableCell align="right"><strong>Cash Refunded</strong></TableCell>
                    <TableCell align="center"><strong>Processed By</strong></TableCell>
                    <TableCell align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {refundHistory.map((refund) => (
                    <TableRow key={refund.id} hover sx={{ '&:hover': { backgroundColor: '#f1f8e9' } }}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {refund.paymentDate || '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={refund.voucherNumber || `REFUND-${refund.id}`}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {refund.billNumber || refund.invoiceNumber || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell><strong>{refund.customerName}</strong></TableCell>
                      <TableCell>{refund.customerPhone || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: '220px' }}>
                        <Tooltip title={refund.reason || 'Customer Return'}>
                          <Chip
                            label={refund.reason || 'Customer Return'}
                            size="small"
                            sx={{
                              backgroundColor: '#fff3e0',
                              color: '#b78103',
                              maxWidth: '200px',
                              textOverflow: 'ellipsis'
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={refund.paymentMode || 'Cash'} size="small" color="success" />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#b71c1c', fontSize: '0.95rem' }}>
                        -₹{(parseFloat(refund.refundAmount) || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', color: '#555' }}>
                        {refund.processedBy || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Print Cash Refund Voucher">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => printRefundVoucherDirect(refund)}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, backgroundColor: '#fff' }}>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default MedicineManagement;
